import qs from 'qs';
import oikosApi from '../../../services/oikos';
import { sendFormEmail } from '../../../utils/fc';
import { notifyError, notifyWarning } from '../../../utils/notify';
import { linkProducaoVM } from '../../../utils/linksOikos/link';
import { EMAIL_TEXT_ENVIAR_FORNECEDOR, buildPedidoCompraEmailContextHtml } from '../ModalFormDisparoEmails/pedidoCompraEmailTexts';
import {
  getContrFornDocumentosObrigatoriosIds,
  resolveDocumentosObrigatoriosOptionsForMinuta,
} from './contrFornExtendedFields';
import { mergeMinutaFornecedorDocIntoHashIds } from './contrFornMinutaRules';
import { buildContrFornMinutaData } from './contrFornMinutaData';
import {
  buildContratacaoHashFiles,
  fetchCompraCotacaoById,
  fetchContratadoRecord,
  fetchContrFornRecordForEmail,
  resolveCompraCotacaoDataForHash,
  resolveContratacaoEmailDestino,
} from './contrFornEmailUtils';

const b64urlEncode = (str) => {
  const base64 = btoa(unescape(encodeURIComponent(str)));
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
};

const montaURLFormContratacaoFornecedor = (data) => (
  `${linkProducaoVM}formContratacaoFornecedor.php#${b64urlEncode(JSON.stringify(data))}`
);

const formatCompraCotacaoInfoCurrency = (value) => (
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value || 0)).replace(/\u00a0/g, ' ')
);

const buildCompraCotacaoInfoForFornecedorEmail = (source = {}) => JSON.stringify({
  informacoes_contrato: {
    valor_global: formatCompraCotacaoInfoCurrency(source.compraCotacao_vlr),
    objeto_resumido: String(source.compraCotacao_desc || '').trim(),
  },
});

async function persistCompraCotacaoInfo(compraCotacaoSource = {}) {
  const compraCotacaoId = Number(compraCotacaoSource?.compraCotacao_id) || 0;
  const compraCotacao_info = buildCompraCotacaoInfoForFornecedorEmail(compraCotacaoSource);
  const next = { ...compraCotacaoSource, compraCotacao_info };

  if (!compraCotacaoId) return next;

  await oikosApi.post('/', {
    action: 'update',
    table: 'compraCotacao',
    data: {
      compraCotacao_id: compraCotacaoId,
      compraCotacao_info,
    },
  });

  return next;
}

async function fetchPedidoCompraById(pedidoCompraId) {
  const id = Number(pedidoCompraId) || 0;
  if (!id) return null;
  try {
    const res = await oikosApi.get(`/?${qs.stringify({
      action: 'get',
      table: 'pedidoCompra',
      data: { pedidoCompra_id: id },
    })}`);
    return res?.data?.[0] || null;
  } catch (error) {
    console.error('Erro ao buscar pedidoCompra:', error);
    return null;
  }
}

async function fetchContrFornEventos(contrFornId) {
  const id = Number(contrFornId) || 0;
  if (!id) return [];
  try {
    const res = await oikosApi.get(`/?${qs.stringify({
      action: 'get',
      table: 'contrFornEvento',
      data: {
        contrForn_id: id,
        contrFornEvento_ativo: 1,
      },
      order: 'contrFornEvento_num ASC',
    })}`);
    return res?.data || [];
  } catch (error) {
    console.error('Erro ao buscar parcelas do contrato:', error);
    return [];
  }
}

async function fetchContrFornDocuments(contrFornId) {
  const id = Number(contrFornId) || 0;
  if (!id) return [];

  try {
    const response = await oikosApi.get(`/?${qs.stringify({
      action: 'get',
      table: 'documentoVinc',
      data: {
        documentoVinc_tabRef: 'contrForn',
        documentoVinc_tabId: id,
        documentoVinc_ativo: 1,
      },
      fields: {
        documentoVinc_id: 'documentoVinc_id',
        documento_id: 'documento_id',
        '(SELECT documento_desc FROM documento WHERE documento.documento_id = documentoVinc.documento_id LIMIT 1)': 'documento_desc',
        '(SELECT documento_ativo FROM documento WHERE documento.documento_id = documentoVinc.documento_id LIMIT 1)': 'documento_ativo',
        '(SELECT arquivo_caminho FROM arquivo WHERE arquivo.arquivo_tabRef = "documento" AND arquivo.arquivo_tabId = documentoVinc.documento_id AND arquivo.arquivo_ativo = 1 ORDER BY arquivo_id DESC LIMIT 1)': 'arquivo_caminho',
      },
    })}`);

    return (response?.data || [])
      .filter((doc) => Number(doc?.documento_ativo) === 1 && (doc?.arquivo_caminho || doc?.documento_desc))
      .map((doc) => ({
        documento_id: doc.documento_id,
        documento_desc: doc.documento_desc,
        arquivo_caminho: doc.arquivo_caminho,
        link: doc.arquivo_caminho ? `${linkProducaoVM}${doc.arquivo_caminho}` : null,
      }));
  } catch (error) {
    console.error('Erro ao buscar documentos do contrato:', error);
    return [];
  }
}

async function fetchOrcItemSelectOption(orcItemId) {
  const id = Number(orcItemId) || 0;
  if (!id) return null;
  try {
    const res = await oikosApi.get(`/?${qs.stringify({
      action: 'get',
      table: 'viewRubr_OrcItem',
      fields: {
        orcItem_id: 'value',
        item_orcamentario: 'label',
        nivel2_id: 'nivel2_id',
        "(SELECT pf_emailProf FROM pf WHERE pf.pf_id = (SELECT pf_id FROM orcItem WHERE orcItem.orcItem_id = viewRubr_OrcItem.orcItem_id LIMIT 1) LIMIT 1)": 'pf_emailProf',
      },
      data: {
        orcItem_id: id,
        orcItem_ativo: 1,
      },
    })}`);
    const row = res?.data?.[0];
    if (!row) return null;
    return {
      value: row.value ?? id,
      label: row.label || String(id),
      nivel2_id: row.nivel2_id,
      pf_emailProf: row.pf_emailProf || null,
    };
  } catch (error) {
    console.error('Erro ao buscar orcItem:', error);
    return null;
  }
}

async function fetchTipoContratoLabel(contrFornTpId) {
  const id = Number(contrFornTpId) || 0;
  if (!id) return [];
  try {
    const res = await oikosApi.get(`/?${qs.stringify({
      action: 'get',
      table: 'contrFornTp',
      data: { contrFornTp_id: id },
    })}`);
    const row = res?.data?.[0];
    if (!row) return [];
    return [{ value: id, label: row.contrFornTp_desc || String(id) }];
  } catch (error) {
    return [];
  }
}

async function fetchTipoMinutaLabel(tpMinutaId) {
  const id = Number(tpMinutaId) || 0;
  if (!id) return [];
  try {
    const res = await oikosApi.get(`/?${qs.stringify({
      action: 'get',
      table: 'tpMinuta',
      data: { tpMinuta_id: id },
    })}`);
    const row = res?.data?.[0];
    if (!row) return [];
    return [{ value: id, label: row.tpMinuta_nome || row.tpMinuta_desc || String(id) }];
  } catch (error) {
    return [];
  }
}

function pickPdfAndFormDocuments(documents = []) {
  const pdfCandidates = documents.filter((doc) => {
    const desc = String(doc.documento_desc || doc.arquivo_caminho || '').toLowerCase();
    return desc.endsWith('.pdf') || desc.includes('contratacao_') || desc.includes('resumo');
  });
  const pdfDocument = pdfCandidates[pdfCandidates.length - 1] || documents[documents.length - 1] || null;
  const formDocuments = documents.filter((doc) => (
    !pdfDocument || Number(doc.documento_id) !== Number(pdfDocument.documento_id)
  ));
  return { pdfDocument, formDocuments };
}

/**
 * Monta o mesmo link do formulário público enviado por e-mail ao fornecedor.
 * Não envia e-mail. persistInfo=true grava compraCotacao_info (fluxo pós-aprovação).
 */
export async function buildFormContratacaoFornecedorLinkByContrFornId(contrFornId, { persistInfo = false } = {}) {
  const id = Number(contrFornId) || 0;
  if (!id) {
    notifyError('Contrato não encontrado para montar o link do formulário.');
    return null;
  }

  try {
    const contrFornRecord = await fetchContrFornRecordForEmail({ contrFornId: id });
    if (!contrFornRecord?.contrForn_id) {
      notifyError('Contrato não encontrado para montar o link do formulário.');
      return null;
    }

    let compraCotacaoSource = await fetchCompraCotacaoById(contrFornRecord.compraCotacao_id);
    if (!compraCotacaoSource) {
      notifyError('Cotação do contrato não encontrada para montar o link do formulário.');
      return null;
    }

    compraCotacaoSource = {
      ...compraCotacaoSource,
      orcItem_id: compraCotacaoSource.orcItem_id || contrFornRecord.orcItem_id,
    };

    const compraCotacaoWithInfo = persistInfo
      ? await persistCompraCotacaoInfo(compraCotacaoSource)
      : {
        ...compraCotacaoSource,
        compraCotacao_info: buildCompraCotacaoInfoForFornecedorEmail(compraCotacaoSource),
      };
    const compraCotacaoForHash = await resolveCompraCotacaoDataForHash(
      compraCotacaoWithInfo,
      contrFornRecord,
    );

    const destino = await resolveContratacaoEmailDestino({
      compraCotacaoData: compraCotacaoWithInfo,
      contrFornRecord,
    });

    const pedidoCompraData = await fetchPedidoCompraById(
      compraCotacaoWithInfo.pedidoCompra_id || compraCotacaoForHash?.pedidoCompra_id,
    );
    const listaParcelas = await fetchContrFornEventos(id);
    const documents = await fetchContrFornDocuments(id);
    const { pdfDocument, formDocuments } = pickPdfAndFormDocuments(documents);
    const files = buildContratacaoHashFiles(pdfDocument, formDocuments);

    const orcItemOption = await fetchOrcItemSelectOption(
      contrFornRecord.orcItem_id || compraCotacaoWithInfo.orcItem_id,
    );
    const selOrcItems = orcItemOption ? [orcItemOption] : [];
    const selTipoContrato = await fetchTipoContratoLabel(contrFornRecord.contrFornTp_id);
    const selTiposDeMinuta = await fetchTipoMinutaLabel(contrFornRecord.tpMinuta_id);

    const contratado = await fetchContratadoRecord(
      contrFornRecord.contrForn_parteTab,
      contrFornRecord.contrForn_parteId,
    );
    const selPfsFornecedor = contrFornRecord.contrForn_parteTab === 'pf' && contratado
      ? [{
        value: Number(contrFornRecord.contrForn_parteId),
        label: contratado.pf_nomeExibicao || contratado.pf_nome || '',
        pf_emailProf: contratado.pf_emailProf,
      }]
      : [];
    const selPjsFornecedor = contrFornRecord.contrForn_parteTab === 'pj' && contratado
      ? [{
        value: Number(contrFornRecord.contrForn_parteId),
        label: contratado.pj_razaosocial || contratado.pj_nomefanta || '',
        pj_emailPrin: contratado.pj_emailPrin,
      }]
      : [];

    const documentosIdsForHash = mergeMinutaFornecedorDocIntoHashIds(
      getContrFornDocumentosObrigatoriosIds(contrFornRecord),
      {
        tpMinutaId: contrFornRecord.tpMinuta_id,
        selTiposDeMinuta,
        tpMinutaNome: contrFornRecord.tpMinuta_nome,
      },
    );
    const contrFornForHash = {
      ...contrFornRecord,
      contrForn_documentosObrigatorios: documentosIdsForHash,
    };
    const documentosOptionsForEmail = await resolveDocumentosObrigatoriosOptionsForMinuta(
      contrFornForHash,
      [],
    );

    const trimContrFornDesc = () => String(contrFornRecord.contrForn_desc || '').trim();

    const minutaData = buildContrFornMinutaData({
      contrFornData: contrFornForHash,
      listaParcelas,
      selOrcItems,
      selPfsFornecedor,
      selPjsFornecedor,
      selCentroDeCusto: [],
      selProjetos: [],
      selTipoContrato,
      selTiposDeMinuta,
      documentosObrigatoriosOptions: documentosOptionsForEmail,
      showDocumentosObrigatorios: documentosIdsForHash.length > 0,
      savedAnexos: formDocuments,
      filesContrForn: [],
      trimContrFornDesc,
    });

    const payload = {
      compraCotacaoData: compraCotacaoForHash || compraCotacaoWithInfo,
      pedidoCompraData: pedidoCompraData || {},
      contrFornData: contrFornForHash,
      minutaData,
      contratado: contratado || null,
      email: destino?.email || null,
      nome: destino?.nome || null,
      identificador: destino?.identificador || null,
      pf_emailProf: orcItemOption?.pf_emailProf
        || contrFornRecord?.pf_emailProf
        || compraCotacaoForHash?.pf_emailProf
        || compraCotacaoWithInfo?.pf_emailProf
        || null,
      ...(files.length > 0 ? { files } : {}),
    };

    return {
      formLink: montaURLFormContratacaoFornecedor(payload),
      destino: destino || null,
      pedidoCompraData: pedidoCompraData || {},
      compraCotacaoForHash: compraCotacaoForHash || compraCotacaoWithInfo,
      id,
    };
  } catch (error) {
    console.error('Erro ao montar o link do formulário de contratação:', error);
    notifyError('Não foi possível montar o link do formulário do fornecedor.');
    return null;
  }
}

/**
 * Envia o e-mail de contratação ao fornecedor a partir de um contrForn_id já persistido.
 * Usado após aprovação da task (taskTp_id = 3) — mesmo fluxo do envio imediato (&lt; 10k).
 */
export async function sendContratacaoFornecedorEmailByContrFornId(contrFornId) {
  const built = await buildFormContratacaoFornecedorLinkByContrFornId(contrFornId, { persistInfo: true });
  if (!built?.formLink) return false;

  if (!built.destino?.email) {
    notifyWarning(
      'Contrato aprovado, mas o contratado não possui e-mail (pf_emailProf / pj_emailPrin) para enviar a notificação.',
    );
    return false;
  }

  try {
    await sendFormEmail({
      emailDest: built.destino.email,
      nomeDest: built.destino.nome,
      formLink: built.formLink,
      mensagemIntro: EMAIL_TEXT_ENVIAR_FORNECEDOR.mensagemIntro,
      formLinkLabel: EMAIL_TEXT_ENVIAR_FORNECEDOR.formLinkLabel,
      corpoTitulo: EMAIL_TEXT_ENVIAR_FORNECEDOR.corpoTitulo,
      corpo: buildPedidoCompraEmailContextHtml({
        pedidoCompraData: built.pedidoCompraData,
        compraCotacaoData: built.compraCotacaoForHash,
      }) || undefined,
      emailEnvioTabRef: 'contrForn',
      emailEnvioTabId: built.id,
    }, EMAIL_TEXT_ENVIAR_FORNECEDOR.assunto);

    return true;
  } catch (error) {
    console.error('Erro ao enviar e-mail de contratação após aprovação:', error);
    notifyError('Contrato aprovado, mas falha ao enviar o e-mail de contratação ao fornecedor.');
    return false;
  }
}
